import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { faStore, faTruck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'next-i18next';

import { types } from '@boss/boss-client';
import { useRouter } from '@boss/hooks';
import type { DeliveryMethod } from '@boss/services/client';
import { Link, RadioGroup } from '@boss/ui';

import { homeDeliverySlugs } from '../../utils';
import AfhaalpuntDropdown from '../AfhaalpuntDropdown';

type Props = {
  afhaalpuntId: string | null;
  favouriteShopId?: string | null;
  levermethode: DeliveryMethod;
  onAfhaalpuntChange: (afhaalpuntId: string | null) => void;
  onLevermethodeChange: (levermethode: DeliveryMethod) => void;
  stores: types.Store[];
  variant: 'primary' | 'secondary';
};

/**
 * [46231] sv04 levermethode selector — replaces the PDP stock overlay
 * (DeliveryPromises tiles + "Winkelvoorraad" trigger + StockModal).
 * Controlled and presentation-only: the selection state and its
 * page-scoped reset live in usePdpStock.
 *
 * `variant` mirrors productPanelVariant:
 *  - secondary (B2B): radio list + afhaalpunt dropdown below (levermethode.title)
 *  - primary (B2C): selectable cards, dropdown inline in the pickup card (levermethode.b2cTitle)
 *
 * Approved deviations from the Figma frames: option labels follow the work
 * item AC verbatim ("Levering aan huis" / "Afhaling in een afhaalpunt") and
 * the B2C cards carry no price badges.
 */
const LevermethodeSelection = ({
  afhaalpuntId,
  favouriteShopId,
  levermethode,
  onAfhaalpuntChange,
  onLevermethodeChange,
  stores,
  variant,
}: Props) => {
  const { locale } = useRouter();
  const { t } = useTranslation('product');

  const isPickup = levermethode === 'Pickup';
  const selectedStoreName = stores.find(store => store.id === afhaalpuntId)?.name;
  const isFavourite = !!afhaalpuntId && afhaalpuntId === favouriteShopId;
  const chooseAfhaalpuntLabel = t('productPanel.levermethode.chooseAfhaalpunt');
  // ponytail: typed const instead of an inline t() — i18next's TFunction
  // only infuses `string` into plain-string slots, not union props (aria-label).
  const b2cTitle: string = t('productPanel.levermethode.b2cTitle');

  /**
   * [46231] greyed-out keep-value: switching back to "Levering aan huis"
   * keeps the chosen afhaalpunt visible as a disabled display — the value
   * itself is never cleared (usePdpStock owns it).
   */
  const afhaalpuntSelection = isPickup ? (
    <AfhaalpuntDropdown
      favouriteShopId={favouriteShopId ?? undefined}
      onChange={store => onAfhaalpuntChange(store.id)}
      searchLabel={chooseAfhaalpuntLabel}
      selectLabel={chooseAfhaalpuntLabel}
      stores={stores}
      value={afhaalpuntId}
    />
  ) : (
    <div
      aria-disabled="true"
      className="border-1 border-generic-gray rounded-30 flex min-w-48 items-center justify-between p-2.5 pl-4 opacity-50"
      data-testid="afhaalpunt-disabled"
    >
      <span className="mr-3">{selectedStoreName ?? chooseAfhaalpuntLabel}</span>
      <FontAwesomeIcon icon={faChevronDown} size="sm" />
    </div>
  );

  const favouriteBadge = isFavourite ? (
    <span className="small text-green">{t('productPanel.levermethode.favourite')}</span>
  ) : null;

  const disclaimer = (
    <Link className="caption underline" href={homeDeliverySlugs?.[locale]}>
      {t('productPanel.levermethode.disclaimer')}
    </Link>
  );

  if (variant === 'primary') {
    return (
      <div className="flex flex-col gap-3">
        <span className="font-bold">{t('productPanel.levermethode.b2cTitle')}</span>
        {/* [46231] gate feedback: cards stack vertically, full width — never side by side. */}
        <div aria-label={b2cTitle} className="flex flex-col gap-4" role="radiogroup">
          <label className="cursor-pointer">
            <input
              checked={levermethode === 'Delivery'}
              className="peer sr-only"
              name="levermethode"
              onChange={() => onLevermethodeChange('Delivery')}
              type="radio"
              value="Delivery"
            />
            <div className="border-1 border-generic-gray rounded-30 flex flex-col gap-2 p-4 peer-checked:border-black peer-focus-visible:border-black peer-focus-visible:ring-2 peer-focus-visible:ring-black">
              <FontAwesomeIcon className="text-delivery-icon" icon={faTruck} />
              <span className="font-bold">{t('productPanel.levermethode.delivery')}</span>
              <span className="small text-gray">{t('productPanel.levermethode.deliverySub')}</span>
            </div>
          </label>
          <label className="cursor-pointer">
            <input
              checked={isPickup}
              className="peer sr-only"
              name="levermethode"
              onChange={() => onLevermethodeChange('Pickup')}
              type="radio"
              value="Pickup"
            />
            <div className="border-1 border-generic-gray rounded-30 flex flex-col gap-2 p-4 peer-checked:border-black peer-focus-visible:border-black peer-focus-visible:ring-2 peer-focus-visible:ring-black">
              <FontAwesomeIcon className="text-delivery-icon" icon={faStore} />
              <span className="font-bold">{t('productPanel.levermethode.pickup')}</span>
              <div className="flex items-center gap-2">
                {afhaalpuntSelection}
                {favouriteBadge}
              </div>
            </div>
          </label>
        </div>
        {disclaimer}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <span className="font-bold">{t('productPanel.levermethode.title')}</span>
      <RadioGroup
        name="levermethode"
        onChange={value => onLevermethodeChange(value === 'Pickup' ? 'Pickup' : 'Delivery')}
        options={[
          {
            label: (
              <div className="flex flex-col gap-1">
                <span>{t('productPanel.levermethode.delivery')}</span>
                <span className="text-gray text-sm italic">{t('productPanel.levermethode.deliverySub')}</span>
              </div>
            ),
            value: 'Delivery',
          },
          { label: <span>{t('productPanel.levermethode.pickup')}</span>, value: 'Pickup' },
        ]}
        value={levermethode}
      />
      <div className="mt-1 flex items-center gap-2">
        {afhaalpuntSelection}
        {favouriteBadge}
      </div>
      {disclaimer}
    </div>
  );
};

export default LevermethodeSelection;
